import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { CheckCircle2, Mail } from 'lucide-react';

interface PluginThankYouDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pluginName: string;
  customerEmail?: string;
}

export const PluginThankYouDialog = ({ 
  open, 
  onOpenChange, 
  pluginName,
  customerEmail 
}: PluginThankYouDialogProps) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <div className="flex justify-center mb-4">
            <div className="rounded-full bg-green-100 dark:bg-green-900/30 p-3">
              <CheckCircle2 className="w-12 h-12 text-green-600 dark:text-green-400" />
            </div>
          </div>
          <DialogTitle className="text-2xl text-center">Payment Successful!</DialogTitle>
          <DialogDescription className="text-center text-base">
            Thank you for purchasing {pluginName} Premium
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Email Instructions */}
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Mail className="w-5 h-5 text-primary mt-0.5 shrink-0" />
              <div>
                <h4 className="font-semibold mb-2">Check Your Email</h4>
                <p className="text-sm text-muted-foreground">
                  Download instructions and your license key have been sent to{' '}
                  {customerEmail ? (
                    <span className="font-medium text-foreground">{customerEmail}</span>
                  ) : (
                    'your email address'
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* What's Next */}
          <div className="space-y-3">
            <h4 className="font-semibold">What's next?</h4>
            <ol className="space-y-3 text-sm">
              <li className="flex gap-3">
                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-primary font-semibold shrink-0">
                  1
                </span>
                <span>Check your inbox for the download link and license key</span>
              </li>
              <li className="flex gap-3">
                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-primary font-semibold shrink-0">
                  2
                </span>
                <span>Download and install the plugin on your WordPress site</span>
              </li>
              <li className="flex gap-3">
                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-primary font-semibold shrink-0">
                  3
                </span>
                <span>Activate your premium license to unlock all features</span>
              </li>
            </ol>
          </div>

          {/* Support Notice */}
          <div className="bg-muted/30 rounded-lg p-4">
            <p className="text-sm text-center">
              Need help? Our premium support team is here for you.
              <br />
              <a 
                href="mailto:support@lovabuild.com" 
                className="text-primary hover:underline font-medium"
              >
                support@lovabuild.com
              </a>
            </p>
          </div>

          <Button
            onClick={() => onOpenChange(false)}
            className="w-full"
            size="lg"
          >
            Got it, thanks!
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
